import { useCallback, useRef, useState } from 'react';
import { loginZirveNova, smsControlZirveNova } from '../utils/zirveNovaRequest';
import { getZirveNovaResponseData, getZirveNovaSmsChallenge, getZirveNovaSmsChallengeFromError } from '../utils/zirveNovaAuth';

export default function useZirveNovaAuthentication({ onAuthenticated, onError }) {
    const [smsChallenge, setSmsChallenge] = useState(null);
    const [loginLoading, setLoginLoading] = useState(false);
    const [smsLoading, setSmsLoading] = useState(false);
    const onAuthenticatedRef = useRef(onAuthenticated);
    const onErrorRef = useRef(onError);

    onAuthenticatedRef.current = onAuthenticated;
    onErrorRef.current = onError;

    const handleResponse = useCallback(async (response) => {
        const challenge = getZirveNovaSmsChallenge(response);

        if (challenge) {
            setSmsChallenge(challenge);
            return;
        }

        setSmsChallenge(null);
        await onAuthenticatedRef.current?.(getZirveNovaResponseData(response));
    }, []);

    const handleError = useCallback((error) => {
        const refreshedChallenge = getZirveNovaSmsChallengeFromError(error);

        if (refreshedChallenge) {
            setSmsChallenge((currentChallenge) => ({
                sessionData: refreshedChallenge.sessionData,
                smsDescription: refreshedChallenge.smsDescription || currentChallenge?.smsDescription || ''
            }));
        }

        onErrorRef.current?.(error);
    }, []);

    const authenticate = useCallback(
        async (username, password) => {
            setLoginLoading(true);

            try {
                const response = await loginZirveNova(username, password);
                await handleResponse(response);
            } catch (error) {
                handleError(error);
            } finally {
                setLoginLoading(false);
            }
        },
        [handleError, handleResponse]
    );

    const verifySms = useCallback(
        async (smsCode) => {
            if (!smsChallenge?.sessionData) {
                return;
            }

            setSmsLoading(true);

            try {
                const response = await smsControlZirveNova(smsChallenge.sessionData, smsCode);
                await handleResponse(response);
            } catch (error) {
                handleError(error);
            } finally {
                setSmsLoading(false);
            }
        },
        [handleError, handleResponse, smsChallenge?.sessionData]
    );

    const cancelSms = useCallback(() => {
        setSmsChallenge(null);
    }, []);

    return {
        authenticate,
        cancelSms,
        loginLoading,
        smsDescription: smsChallenge?.smsDescription || '',
        smsLoading,
        smsVisible: Boolean(smsChallenge?.sessionData),
        verifySms
    };
}

